import { useDataCache } from '#nuxt-multi-cache/composables';
import { type Pinia } from 'pinia';
import { type H3Event } from 'h3';
import { type AxiosResponse } from 'axios';
import { type useAxios } from '@/utils/axios/useAxios';
import type { ApiConfig, RequestConfig, Headers, ApiResponse } from '@/types/api';

export default class AbstractApi {
  config: ApiConfig;
  baseUrl: string;
  basePath: string;
  headers: Headers;
  client: ReturnType<typeof useAxios>;
  event?: H3Event;
  pinia?: Pinia;

  constructor(config: ApiConfig) {
    // Only needed for certain client-only edge cases, so we should be safe doing this
    if (import.meta.client) {
      this.pinia = useNuxtApp().$pinia;
    }

    this.config = config;
    this.baseUrl = '';
    this.basePath = '';
    this.headers = {};
    this.client = config.client;
    this.event = config.event;

    if (import.meta.dev && !this.client) {
      throw createError({
        statusCode: 500,
        statusMessage: `Missing 'client' in '${this.constructor.name}'`,
      });
    }
  }

  async sendRequest(config: RequestConfig): Promise<ApiResponse> {
    // Cache is only enabled server-side for requests with 'cache: true'
    const isCacheableMethod = config.method.toLowerCase() === 'get';
    const shouldCache = import.meta.server && this.config.multiCache.data && config.cache && isCacheableMethod;

    let cacheKey, addToCache, ttl;

    if (shouldCache) {
      cacheKey = generateCacheKeyRequest(config);
      ttl = parseInt(this.config.cache.data.ttl, 10);

      const cache = await useDataCache<ApiResponse>(cacheKey, this.event);

      // POTENTIAL CACHE HIT: got cache value, doing stale check next
      if (cache.value) {
        const isStale = !cache.value.createdAt ? true : Date.now() - cache.value.createdAt > ttl;

        if (!isStale) {
          // CACHE HIT
          return cache.value;
        }
      }

      // CACHE MISS: assign util to add fresh cache after the request
      addToCache = cache.addToCache;
    }

    let response: AxiosResponse;

    try {
      response = await this.client.request(config);
    } catch (error: any) {
      if (import.meta.client) {
        if (isResponseError(error, 401, REQUIRES_HARD_LOGIN_ERROR)) {
          useUserStore(this.pinia).softLogin = true;
        }

        // Access token gets invalidated after a long time (app in background)
        // In this case, do a browser refresh to get a refreshed token for WebViews (in app)
        if (
          useMainStore(this.pinia).isAppview &&
          !useAppBridge().isActiveCDC &&
          !config.isTokenApi &&
          isResponseError(error, 401)
        ) {
          logger.debug('Reloading Page due to unauthorized ajax request in appview');
          window.location.reload();
          return {} as ApiResponse;
        }
      }

      // This may be the entry point to handle a bunch of errors more gracefully,
      // e.g. 500 addrnotfound api.fressnapf... on missing internet connection
      throw error;
    }

    if (shouldCache && addToCache) {
      const responseCloned = deepClone({
        // Response data
        config,
        data: response.data,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,

        // Cache metadata
        id: cacheKey,
        ttl,
        createdAt: Date.now(),
      }) satisfies ApiResponse;

      await addToCache(
        // Response data
        responseCloned,

        // Tags array, currently not used
        undefined,

        // Max age in seconds
        ttl,
      );

      return responseCloned;
    }

    return {
      config,
      data: response.data,
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
    };
  }

  async request(config: RequestConfig, shouldLogData = true) {
    config.url = this.getURL(config.url, config.version);
    config.headers = this.getHeaders(config.headers, config.abTests);

    const response = await this.sendRequest(config);

    if (logger.isEnabled('debug')) {
      logger.debug(`${config.method.toUpperCase()} ${config.url}`, this.getLogData(shouldLogData, config, response));
    }

    return response;
  }

  getURL(endpoint: string, version: string) {
    const basePath = version ? `/${version}` : this.basePath;
    return `${this.baseUrl}${basePath}${endpoint}`;
  }

  getHeaders(headers: Headers, abTests: Record<string, any>) {
    if (!headers) {
      return this.headers;
    }

    if (abTests) {
      return {
        ...this.headers,
        ...headers,
        'FN-UX': JSON.stringify(abTests || {}),
      };
    }

    return {
      ...this.headers,
      ...headers,
    };
  }

  setAccessToken(accessToken?: string) {
    if (!accessToken) {
      delete this.headers.Authorization;
    } else {
      this.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  getLogData(shouldLogData: boolean, config: RequestConfig, response?: ApiResponse) {
    let configCopy;

    try {
      configCopy = JSON.parse(JSON.stringify(config));

      if (!shouldLogData) {
        delete configCopy.data;
      } else {
        // Always delete sensitive user data
        delete configCopy.data?.username;
        delete configCopy.data?.password;
      }

      // Delete redundant stuff
      delete configCopy.version;
      delete configCopy.lang;

      // Remove empty stuff to keep logs clean
      if (!Object.keys(configCopy.headers ?? {}).length) {
        delete configCopy.headers;
      }
      if (!Object.keys(configCopy.params ?? {}).length) {
        delete configCopy.params;
      }

      // Add cache info if available
      if (response?.ttl) {
        const remaining = response.ttl - (Date.now() - response.createdAt);
        configCopy.cache = `${(remaining / 1000).toFixed(0)}s`;
      }
    } catch (error) {
      logger.error('Error occurred while parsing request config:', error);
    }

    delete configCopy.method;
    delete configCopy.url;

    if (!Object.keys(configCopy).length) {
      return '';
    }

    return logger.inspect(configCopy);
  }
}
